'use client';

import type { HeaderLabels } from './header-types';
import type { MouseEvent } from 'react';

import { SignInDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import Link from '@eliadmin/core/src/components/link';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useQuery } from '@tanstack/react-query';
import { CartIcon, HeartIcon, UserIcon } from './header-icons';
import { MobileHeaderSearch } from './header-search';

const CART_COUNT_LIMIT = 99;

const actionClassName =
  'relative flex flex-col items-center gap-0.5 rounded-xl p-1.5 text-[.72rem] font-medium transition-colors duration-150 hover:bg-[#f3f5f7] min-[901px]:px-2.5';
/**
 * Labels show from 901 px; below that the links are icon-only and named by `aria-label`.
 * (`sr-only` cannot be undone here: Font Awesome's global `.sr-only` loads after Tailwind.)
 */
const actionLabelClassName = 'hidden min-[901px]:block';

type HeaderActionsProps = {
  labels: Pick<HeaderLabels, 'signIn' | 'account' | 'favorites' | 'cart' | 'search'>;
};

export function HeaderActions({ labels }: HeaderActionsProps) {
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const isLoggedIn = useCustomerStore(state => state.isInitialized && !!state.me);
  const canUseGuestFavorites = useGlobalStore(
    state =>
      !!state.templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled,
  );
  const openDialog = useDialogStore(state => state.open);
  // Wait for the customer, otherwise a logged-in visitor's cart is fetched twice (guest, then own).
  const { data: cart } = useQuery({ ...useCartQueryOpts(), enabled: isInitialized });
  const cartCount = cart?.count ?? 0;
  const cartCountLabel = cartCount > CART_COUNT_LIMIT ? `${CART_COUNT_LIMIT}+` : String(cartCount);
  const accountLabel = isLoggedIn ? labels.account : labels.signIn;

  const handleFavoritesClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isLoggedIn || canUseGuestFavorites) return;
    event.preventDefault();
    openDialog({ id: SignInDialog.id, content: <SignInDialog /> });
  };

  return (
    <div className="ml-auto flex shrink-0 gap-1.5">
      <Link
        href={isLoggedIn ? ROUTES.PROFILE() : ROUTES.SIGN_IN()}
        prefetch={false}
        aria-label={accountLabel}
        className={actionClassName}
      >
        <UserIcon />
        <span className={actionLabelClassName}>{accountLabel}</span>
      </Link>
      <Link
        href={isLoggedIn ? ROUTES.FAVORITES() : ROUTES.FAVORITE_PRODUCTS()}
        prefetch={false}
        onClick={handleFavoritesClick}
        aria-label={labels.favorites}
        className={actionClassName}
      >
        <HeartIcon />
        <span className={actionLabelClassName}>{labels.favorites}</span>
      </Link>
      <Link
        href={ROUTES.CART()}
        prefetch={false}
        aria-label={`${labels.cart} ${cartCountLabel}`}
        className={actionClassName}
      >
        <CartIcon />
        <span className={actionLabelClassName}>{labels.cart}</span>
        {' '}
        <span className="absolute right-3 top-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[#e30613] text-[.65rem] font-bold text-white">
          {cartCountLabel}
        </span>
      </Link>
      <MobileHeaderSearch label={labels.search} className={actionClassName} />
    </div>
  );
}
